/**
 * Reading the router's answer to "which models can be added".
 *
 * The Add Models list used to put `answer.models` straight into state. From
 * 2026-08-19 to 2026-10-21 the router answered a bare `0`, so the list held
 * `undefined`, the first loop over it threw, and the whole page went blank
 * with nothing on screen to say why.
 *
 * So the answer is checked here, once, before anything renders it. An answer
 * that cannot be read becomes a failure with a reason, never an empty list:
 * "the router found no models" and "the router's answer made no sense" are
 * opposite claims, and only the second is true when the shape is wrong.
 *
 * No React and no DOM here, so the rule can be tested on its own. The router's
 * own suite also feeds this the route's real answer (tests/model-discovery.test.ts),
 * so a change on either side that breaks the other fails a test.
 */

export interface DiscoveredModel {
  id: string;
  upstreamModelKey: string;
  upstreamProvider: string;
  sourceProviderId: string;
  displayName: string;
  upstreamInputCostPerMillion: number;
  upstreamCachedInputCostPerMillion: number;
  upstreamCacheWriteCostPerMillion: number;
  upstreamOutputCostPerMillion: number;
  supportsCaching: boolean;
  contextWindow: number | null;
  maxOutputTokens: number | null;
}

export type DiscoverReading =
  | {
    ok: true;
    models: DiscoveredModel[];
    /** "openrouter: answered HTTP 401", one per provider that failed. */
    problems: string[];
    /** "anthropic (no API key)", one per provider that was not asked. */
    skipped: string[];
  }
  | { ok: false; reason: string };

/** Describe a value in a few words, for saying what arrived instead of what was expected. */
function describe(value: unknown): string {
  if (value === null) return 'null';
  if (value === undefined) return 'nothing';
  if (typeof value === 'number') return `the number ${value}`;
  if (Array.isArray(value)) return `a list of ${value.length}`;
  if (typeof value === 'object') {
    const keys = Object.keys(value as object);
    return keys.length ? `an object with ${keys.slice(0, 5).join(', ')}` : 'an empty object';
  }
  if (typeof value === 'string') return `the text "${value.slice(0, 40)}"`;
  return `${typeof value} ${String(value)}`;
}

const text = (v: unknown, fallback: string): string => (typeof v === 'string' && v ? v : fallback);
const amount = (v: unknown): number => (typeof v === 'number' && Number.isFinite(v) ? v : 0);
const countOrNull = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null);

export function readDiscoverAnswer(body: unknown): DiscoverReading {
  const answer = body as { models?: unknown; errors?: unknown; skipped?: unknown } | null;
  if (typeof answer !== 'object' || answer === null || !Array.isArray(answer.models)) {
    return { ok: false, reason: `The router's answer had no list of models: it sent ${describe(body)}.` };
  }

  const models: DiscoveredModel[] = [];
  let unreadable = 0;
  for (const entry of answer.models) {
    const m = entry as Record<string, unknown> | null;
    // The two ids are what adding a model writes; without them there is nothing to add.
    if (typeof m !== 'object' || m === null || typeof m.id !== 'string' || !m.id
      || typeof m.upstreamModelKey !== 'string' || !m.upstreamModelKey) {
      unreadable++;
      continue;
    }
    models.push({
      id: m.id,
      upstreamModelKey: m.upstreamModelKey,
      upstreamProvider: text(m.upstreamProvider, ''),
      sourceProviderId: text(m.sourceProviderId, ''),
      displayName: text(m.displayName, m.id),
      upstreamInputCostPerMillion: amount(m.upstreamInputCostPerMillion),
      upstreamCachedInputCostPerMillion: amount(m.upstreamCachedInputCostPerMillion),
      upstreamCacheWriteCostPerMillion: amount(m.upstreamCacheWriteCostPerMillion),
      upstreamOutputCostPerMillion: amount(m.upstreamOutputCostPerMillion),
      supportsCaching: m.supportsCaching === true,
      contextWindow: countOrNull(m.contextWindow),
      maxOutputTokens: countOrNull(m.maxOutputTokens),
    });
  }

  const problems: string[] = [];
  for (const e of Array.isArray(answer.errors) ? answer.errors : []) {
    const { provider, error } = (e ?? {}) as { provider?: unknown; error?: unknown };
    problems.push(`${text(provider, 'unknown provider')}: ${text(error, 'failed without saying why')}`);
  }
  if (unreadable > 0) {
    problems.push(`router: ${unreadable} of the models it sent had no id and were left out`);
  }

  const skipped: string[] = [];
  for (const s of Array.isArray(answer.skipped) ? answer.skipped : []) {
    const { provider, reason } = (s ?? {}) as { provider?: unknown; reason?: unknown };
    skipped.push(`${text(provider, 'unknown provider')} (${text(reason, 'no reason given')})`);
  }

  return { ok: true, models, problems, skipped };
}

/**
 * Which panel the Add Models dialog shows.
 *
 * The failure is checked before the empty list. A failure recorded but checked
 * second is still swallowed, because a failed discovery also has no models.
 */
export function discoverPanel(state: { syncing: boolean; error: string | null; count: number }): 'loading' | 'error' | 'list' | 'empty' {
  if (state.syncing && state.count === 0) return 'loading';
  if (state.error) return 'error';
  if (state.count > 0) return 'list';
  return 'empty';
}
